import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Contrast,
  Crop,
  Crosshair,
  DraftingCompass,
  Maximize2,
  MousePointer2,
  Pause,
  Play,
  RotateCcw,
  Ruler,
  SquareDashed,
  Trash2,
} from 'lucide-react'
import {
  annotationSeriesKey,
  annotationStashGeneration,
  hopSeriesAnnotations,
  maxAnnotationId,
  stashSeriesAnnotations,
  type AnnotationStash,
} from '../lib/annotationStash'
import { isTextEntryTarget } from '../lib/keyboardShortcuts'
import { rulerLengthMillimeters } from '../lib/mpr'
import {
  formatProbeScalar,
  mapIntensityToDisplayGray,
  samplePixelAt,
  type PixelProbeSample,
} from '../lib/pixelProbe'
import { computeRoiStats, formatRoiSummary } from '../lib/roiStats'
import { exportCapturePng, renderAnnotatedSliceCanvas, type CaptureExportResult } from '../lib/sliceCapture'
import { FIT_VIEW, MIN_VIEW_SCALE, zoomAboutPoint, type ViewTransform } from '../lib/sliceView'
import { mapRelativeSliceIndex } from '../lib/volume'
import type { AnatomicalPlane, CropBounds, VolumeData, VolumeSettings } from '../types'

const ZOOM_STEP = 1.12

export interface SliceViewerHandle {
  /**
   * Export an annotated PNG of the current slice (measurements, pins, metadata).
   * Prefers clipboard; falls back to download. Null when no canvas is available.
   */
  capture: () => Promise<CaptureExportResult | null>
  /**
   * Build an annotated canvas for the current slice without downloading.
   * Used by Compare layout to stitch A|B into one PNG.
   */
  captureAnnotatedCanvas: () => HTMLCanvasElement | null
  /** Toggle cine play/pause (no-op when stack has a single slice). */
  toggleCine: () => void
  /** Stop cine without changing the current slice (e.g. before Home/End jumps). */
  pauseCine: () => void
}

const CINE_FPS_OPTIONS = [5, 10, 15] as const
type CineFps = (typeof CINE_FPS_OPTIONS)[number]

const MPR_PLANES: { plane: AnatomicalPlane; label: string; shortLabel: string }[] = [
  { plane: 'axial', label: 'Axial', shortLabel: 'AX' },
  { plane: 'coronal', label: 'Coronal', shortLabel: 'COR' },
  { plane: 'sagittal', label: 'Sagittal', shortLabel: 'SAG' },
]

interface SliceViewerProps {
  volume: VolumeData
  sliceIndex: number
  onSliceChange: (index: number) => void
  volumeSettings: VolumeSettings
  onVolumeSettingsChange: (patch: Partial<VolumeSettings>) => void
  cropBounds: CropBounds
  onCropChange: (bounds: CropBounds) => void
  cropEditing: boolean
  onCropEditingChange: (editing: boolean) => void
  /** Used to pause cine when the viewer layout changes. */
  viewerLayout?: string
  /** Flash a crosshair after a 3D volume pick (token re-triggers the animation). */
  pickFlash?: { token: number; x: number; y: number } | null
  /** Optional A/B badge for side-by-side compare panes. */
  paneLabel?: string
  /** Hide 3D crop controls (compare layout). */
  hideCropControls?: boolean
  /**
   * Controlled pan/zoom. When set with `onViewTransformChange`, parent owns the
   * transform (Compare view-link). Omit for local fit/pan/zoom state.
   */
  viewTransform?: ViewTransform
  onViewTransformChange?: (view: ViewTransform) => void
  /**
   * Controlled viewers normally notify their owner to reset when the series
   * changes. Compare pane B disables this while linked so mounting a new B
   * preserves A and accepts the transform seeded by the parent.
   */
  resetControlledViewOnVolumeChange?: boolean
  /** Controlled single-plane MPR selector; omitted in Compare. */
  slicePlane?: AnatomicalPlane
  acquiredPlane?: AnatomicalPlane
  onSlicePlaneChange?: (plane: AnatomicalPlane) => void
  /**
   * Session stash of 2D marks keyed by seriesId. Owned by the viewer session so
   * series-card clicks, Compare B hops, and layout remounts share one Map.
   */
  annotationStash: AnnotationStash
}

interface CanvasRect {
  left: number
  top: number
  width: number
  height: number
}

type CropInteraction =
  | { type: 'draw'; startX: number; startY: number }
  | { type: 'move'; startX: number; startY: number; bounds: CropBounds }
  | { type: 'resize'; corner: 'nw' | 'ne' | 'sw' | 'se'; bounds: CropBounds }

type MeasurementTool = 'distance' | 'roi' | 'angle'

interface MeasurementPoint {
  x: number
  y: number
}

interface Measurement {
  id: number
  tool: MeasurementTool
  slice: number
  start: MeasurementPoint
  end: MeasurementPoint
  /** Shared vertex for angle (start—vertex—end). */
  vertex?: MeasurementPoint
}

/** In-progress three-click angle build (mirrors draft; ref avoids stale multi-click). */
interface AngleBuild {
  slice: number
  start: MeasurementPoint
  vertex?: MeasurementPoint
}

interface PinnedProbe {
  id: number
  slice: number
  x: number
  y: number
  sample: PixelProbeSample
}

type WindowLevelInteraction = {
  type: 'window-level'
  originX: number
  originY: number
  startWindow: number
  startLevel: number
}

type PointerInteraction = CropInteraction | WindowLevelInteraction | {
  type: 'measurement'
  tool: MeasurementTool
  slice: number
  start: MeasurementPoint
} | {
  type: 'pan'
  startClientX: number
  startClientY: number
  originX: number
  originY: number
}

function clamp01(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value))
}

function orientationLabels(orientation: string) {
  const name = orientation.toLowerCase()
  if (name.includes('sag')) return { top: 'S', right: 'P', bottom: 'I', left: 'A' }
  if (name.includes('cor')) return { top: 'S', right: 'L', bottom: 'I', left: 'R' }
  return { top: 'A', right: 'L', bottom: 'P', left: 'R' }
}

function angleArmPixels(
  from: MeasurementPoint,
  to: MeasurementPoint,
  width: number,
  height: number,
) {
  return Math.hypot((to.x - from.x) * width, (to.y - from.y) * height)
}

function angleIsLargeEnough(measurement: Measurement, width: number, height: number) {
  if (!measurement.vertex) return false
  return (
    angleArmPixels(measurement.start, measurement.vertex, width, height) >= 3
    && angleArmPixels(measurement.end, measurement.vertex, width, height) >= 3
  )
}

function measurementSummary(
  measurement: Measurement,
  volume: VolumeData,
  width: number,
  height: number,
) {
  if (measurement.tool === 'angle') {
    if (!measurement.vertex) return ''
    const spanX = Math.max(1, width - 1) * volume.spacing[0]
    const spanY = Math.max(1, height - 1) * volume.spacing[1]
    const ax = (measurement.start.x - measurement.vertex.x) * spanX
    const ay = (measurement.start.y - measurement.vertex.y) * spanY
    const bx = (measurement.end.x - measurement.vertex.x) * spanX
    const by = (measurement.end.y - measurement.vertex.y) * spanY
    const magA = Math.hypot(ax, ay)
    const magB = Math.hypot(bx, by)
    if (magA < 1e-9 || magB < 1e-9) return ''
    const cos = Math.max(-1, Math.min(1, (ax * bx + ay * by) / (magA * magB)))
    const degrees = (Math.acos(cos) * 180) / Math.PI
    return `${degrees < 10 ? degrees.toFixed(1) : degrees.toFixed(0)}°`
  }

  const deltaX = (measurement.end.x - measurement.start.x) * Math.max(1, width - 1)
  const deltaY = (measurement.end.y - measurement.start.y) * Math.max(1, height - 1)
  if (measurement.tool === 'distance') {
    const millimeters = Math.hypot(deltaX * volume.spacing[0], deltaY * volume.spacing[1])
    return `${millimeters < 10 ? millimeters.toFixed(1) : millimeters.toFixed(0)} mm`
  }

  const stats = computeRoiStats(
    volume,
    {
      start: measurement.start,
      end: measurement.end,
      slice: measurement.slice,
    },
    width,
    height,
  )
  return formatRoiSummary(stats)
}

const TOOL_LABELS: Record<MeasurementTool | 'probe', string> = {
  distance: 'Distance',
  roi: 'ROI',
  angle: 'Angle',
  probe: 'Probe',
}

const TOOL_SORT_ORDER: Record<MeasurementTool | 'probe', number> = {
  distance: 0,
  roi: 1,
  angle: 2,
  probe: 3,
}

/** Normalized image point used to flash a mark after jump-to-slice. */
function measurementFlashPoint(measurement: Measurement): MeasurementPoint {
  if (measurement.tool === 'angle' && measurement.vertex) {
    return { x: measurement.vertex.x, y: measurement.vertex.y }
  }
  return {
    x: (measurement.start.x + measurement.end.x) * 0.5,
    y: (measurement.start.y + measurement.end.y) * 0.5,
  }
}

interface AnnotationInventoryItem {
  key: string
  kind: 'measurement' | 'probe'
  tool: MeasurementTool | 'probe'
  id: number
  slice: number
  summary: string
  flashX: number
  flashY: number
}

interface AnnotationStackState {
  seriesKey: string
  depth: number
  sliceIndex: number
}

interface PreservedSliceTransition {
  /** Safe index rendered before App applies its fractional depth remap. */
  interim: number
  /** Final fractional index that App's MPR depth effect will apply. */
  expected: number
}

export const SliceViewer = forwardRef<SliceViewerHandle, SliceViewerProps>(
  function SliceViewer({
    volume,
    sliceIndex,
    onSliceChange,
    volumeSettings,
    onVolumeSettingsChange,
    cropBounds,
    onCropChange,
    cropEditing,
    onCropEditingChange,
    viewerLayout,
    pickFlash = null,
    paneLabel,
    hideCropControls = false,
    viewTransform,
    onViewTransformChange,
    resetControlledViewOnVolumeChange = true,
    slicePlane,
    acquiredPlane,
    onSlicePlaneChange,
    annotationStash,
  }, forwardedRef) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const viewportRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const interactionRef = useRef<PointerInteraction | null>(null)
    const measurementIdRef = useRef(0)
    const probeIdRef = useRef(0)
    const annotationFlashTokenRef = useRef(0)
    const angleBuildRef = useRef<AngleBuild | null>(null)
    const sliceIndexRef = useRef(sliceIndex)
    /** Shared session Map — series-card / Compare B hops must not clear it. */
    const annotationStashRef = useRef(annotationStash)
    annotationStashRef.current = annotationStash
    /** Generation of the session that owns the currently displayed annotations. */
    const annotationStashGenerationRef = useRef(annotationStashGeneration(annotationStash))
    /** null until first series effect — distinguishes mount rehydrate from live hop. */
    const seriesIdRef = useRef<string | null>(null)
    /** Last rendered annotation stack, including its through-plane sampling depth. */
    const annotationStackRef = useRef<AnnotationStackState | null>(null)
    /** Same-stack depth remaps must not look like user-driven slice navigation. */
    const preservedSliceTransitionRef = useRef<PreservedSliceTransition | null>(null)
    const measurementsRef = useRef<Measurement[]>([])
    const pinnedProbesRef = useRef<PinnedProbe[]>([])
    const viewRef = useRef<ViewTransform>(FIT_VIEW)
    const [canvasRect, setCanvasRect] = useState<CanvasRect | null>(null)
    const [localView, setLocalView] = useState<ViewTransform>(FIT_VIEW)
    const viewControlled = viewTransform !== undefined
    const view = viewControlled ? viewTransform : localView
    const onViewTransformChangeRef = useRef(onViewTransformChange)
    onViewTransformChangeRef.current = onViewTransformChange
    const setView = (next: ViewTransform | ((current: ViewTransform) => ViewTransform)) => {
      const resolved = typeof next === 'function' ? next(viewRef.current) : next
      viewRef.current = resolved
      onViewTransformChangeRef.current?.(resolved)
      if (!viewControlled) setLocalView(resolved)
    }
    const [panning, setPanning] = useState(false)
    const [measurementTool, setMeasurementTool] = useState<MeasurementTool | null>(null)
    const [probeTool, setProbeTool] = useState(false)
    /** Pane-local display polarity; never written into volume data or capture metadata. */
    const [invertDisplay, setInvertDisplay] = useState(false)
    const [measurements, setMeasurements] = useState<Measurement[]>([])
    const [measurementDraft, setMeasurementDraft] = useState<Measurement | null>(null)
    const [probeHover, setProbeHover] = useState<{
      x: number
      y: number
      sample: PixelProbeSample
    } | null>(null)
    const [pinnedProbes, setPinnedProbes] = useState<PinnedProbe[]>([])
    measurementsRef.current = measurements
    pinnedProbesRef.current = pinnedProbes
    const [windowLevelDrag, setWindowLevelDrag] = useState<{ window: number; level: number } | null>(null)
    const [cinePlaying, setCinePlaying] = useState(false)
    const [cineFps, setCineFps] = useState<CineFps>(10)
    const [activePickFlash, setActivePickFlash] = useState<{
      token: number
      x: number
      y: number
    } | null>(null)
    /** Series inventory jump — highlights matching mark after slice change. */
    const [annotationFlash, setAnnotationFlash] = useState<{
      token: number
      kind: 'measurement' | 'probe'
      id: number
    } | null>(null)
    const [width, height, depth] = volume.dimensions
    const safeIndex = Math.max(0, Math.min(depth - 1, sliceIndex))
    const labels = orientationLabels(volume.orientation)
    const hasPlaneSwitch = slicePlane !== undefined && onSlicePlaneChange !== undefined
    const horizontalSpanMillimeters = Math.max(1, width - 1) * volume.spacing[0]
    const verticalSpanMillimeters = Math.max(1, height - 1) * volume.spacing[1]
    const horizontalRulerMillimeters = rulerLengthMillimeters(horizontalSpanMillimeters)
    const verticalRulerMillimeters = rulerLengthMillimeters(verticalSpanMillimeters)
    sliceIndexRef.current = safeIndex
    const viewTransformed = view.scale > MIN_VIEW_SCALE + 0.001 || Math.abs(view.x) > 0.5 || Math.abs(view.y) > 0.5
    viewRef.current = view

    const toggleCine = () => {
      if (depth <= 1) return
      setCinePlaying((playing) => !playing)
    }

    const pauseCine = () => {
      setCinePlaying(false)
    }

    useImperativeHandle(
      forwardedRef,
      () => {
        const captureAnnotatedCanvas = (): HTMLCanvasElement | null => {
          const canvas = canvasRef.current
          if (!canvas) return null
          const current = measurements.filter((measurement) => measurement.slice === safeIndex)
          const visible = measurementDraft?.slice === safeIndex
            ? [...current, measurementDraft]
            : current
          const showScalar =
            Math.abs(volume.scalarRange[1] - volume.scalarRange[0] - 255) > 1
            || Math.abs(volume.scalarRange[0]) > 0.5
          const pins = pinnedProbes
            .filter((probe) => probe.slice === safeIndex)
            .map((probe) => ({
              x: probe.x,
              y: probe.y,
              intensityLabel: showScalar
                ? `I ${probe.sample.intensity} · ${formatProbeScalar(probe.sample.scalar)}`
                : `I ${probe.sample.intensity}`,
              coordsLabel: `c ${probe.sample.col} · r ${probe.sample.row}`,
            }))
          return renderAnnotatedSliceCanvas({
            source: canvas,
            seriesName: volume.description,
            sliceIndex: safeIndex,
            sliceCount: depth,
            window: volumeSettings.window,
            level: volumeSettings.level,
            labels,
            measurements: visible.map((measurement) => ({
              id: measurement.id,
              tool: measurement.tool,
              start: measurement.start,
              end: measurement.end,
              ...(measurement.vertex ? { vertex: measurement.vertex } : null),
              label: measurementSummary(measurement, volume, width, height),
            })),
            pinnedProbes: pins,
          })
        }

        return {
          captureAnnotatedCanvas,
          capture: async () => {
            const annotated = captureAnnotatedCanvas()
            if (!annotated) return null
            const orientation = volume.orientation.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
            const filename = `lumen-${volume.description.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${orientation}-slice-${safeIndex + 1}.png`
            return exportCapturePng(annotated, filename)
          },

          toggleCine,
          pauseCine,
        }
      },
      [
        depth,
        height,
        labels,
        measurementDraft,
        measurements,
        pinnedProbes,
        safeIndex,
        volume,
        volumeSettings.level,
        volumeSettings.window,
        width,
      ],
    )

    useEffect(() => {
      const canvas = canvasRef.current
      const context = canvas?.getContext('2d', { alpha: false })
      if (!canvas || !context) return

      canvas.width = width
      canvas.height = height
      const image = context.createImageData(width, height)
      const sliceOffset = safeIndex * width * height
      const { window, level } = volumeSettings

      for (let pixel = 0; pixel < width * height; pixel += 1) {
        const value = mapIntensityToDisplayGray(
          volume.data[sliceOffset + pixel] ?? 0,
          window,
          level,
          invertDisplay,
        )
        const target = pixel * 4
        image.data[target] = value
        image.data[target + 1] = value
        image.data[target + 2] = value
        image.data[target + 3] = 255
      }
      context.putImageData(image, 0, 0)
    }, [
      height,
      invertDisplay,
      safeIndex,
      volume.data,
      volumeSettings.level,
      volumeSettings.window,
      width,
    ])

    useEffect(() => {
      const canvas = canvasRef.current
      const stage = stageRef.current
      if (!canvas || !stage) return
      const updateRect = () => {
        // Layout offsets (pre-transform) so the overlay rides with CSS pan/zoom.
        setCanvasRect({
          left: canvas.offsetLeft,
          top: canvas.offsetTop,
          width: canvas.offsetWidth,
          height: canvas.offsetHeight,
        })
      }
      const observer = new ResizeObserver(updateRect)
      observer.observe(canvas)
      observer.observe(stage)
      updateRect()
      return () => observer.disconnect()
    }, [height, width])

    useEffect(() => {
      const seriesKey = annotationSeriesKey(volume.seriesId)
      const previous = annotationStackRef.current

      if (
        previous
        && previous.seriesKey === seriesKey
        && previous.depth !== depth
      ) {
        const remapSlice = (index: number) => (
          mapRelativeSliceIndex(index, previous.depth, depth)
        )
        const expectedSliceIndex = remapSlice(previous.sliceIndex)

        // App owns the controlled slice index. Preserve the draft across both a
        // temporary clamp under the new depth and App's following fractional remap.
        if (safeIndex !== previous.sliceIndex || safeIndex !== expectedSliceIndex) {
          preservedSliceTransitionRef.current = {
            interim: safeIndex,
            expected: expectedSliceIndex,
          }
        }

        setMeasurements((current) => {
          const next = current.map((measurement) => ({
            ...measurement,
            slice: remapSlice(measurement.slice),
          }))
          measurementsRef.current = next
          return next
        })
        setPinnedProbes((current) => {
          const next = current.map((probe) => {
            const slice = remapSlice(probe.slice)
            const sample = samplePixelAt(
              volume,
              slice,
              probe.x,
              probe.y,
              volumeSettings.window,
              volumeSettings.level,
              invertDisplay,
            )
            return { ...probe, slice, ...(sample ? { sample } : null) }
          })
          pinnedProbesRef.current = next
          return next
        })
        setMeasurementDraft((current) => (
          current ? { ...current, slice: remapSlice(current.slice) } : null
        ))

        if (angleBuildRef.current) {
          angleBuildRef.current = {
            ...angleBuildRef.current,
            slice: remapSlice(angleBuildRef.current.slice),
          }
        }
        const interaction = interactionRef.current
        if (interaction?.type === 'measurement') {
          interactionRef.current = {
            ...interaction,
            slice: remapSlice(interaction.slice),
          }
        }

        annotationStackRef.current = {
          seriesKey,
          depth,
          sliceIndex: expectedSliceIndex,
        }
        return
      }

      if (previous?.seriesKey !== seriesKey) {
        preservedSliceTransitionRef.current = null
      }
      annotationStackRef.current = { seriesKey, depth, sliceIndex: safeIndex }
    }, [depth, invertDisplay, safeIndex, volume, volumeSettings.level, volumeSettings.window])

    useEffect(() => {
      const previousSeriesId = seriesIdRef.current
      // Strip synthetic enhanced/recon tags; hop only when base series (or MPR plane) changes.
      const nextSeriesId = annotationSeriesKey(volume.seriesId)
      const currentStashGeneration = annotationStashGeneration(annotationStashRef.current)

      // Synthetic-only seriesId flips (Enhanced ready / Acquired↔Enhanced) must not hop or reset.
      if (previousSeriesId === nextSeriesId) {
        annotationStashGenerationRef.current = currentStashGeneration
        return
      }

      // Series-card / Compare B hop (or mount remount): stash+restore only — never clear.
      const restored = hopSeriesAnnotations(
        annotationStashRef.current,
        previousSeriesId,
        nextSeriesId,
        {
          measurements: measurementsRef.current,
          pinnedProbes: pinnedProbesRef.current,
        },
        annotationStashGenerationRef.current,
      )
      setMeasurements(restored.measurements as Measurement[])
      setPinnedProbes(restored.pinnedProbes as PinnedProbe[])
      const highId = maxAnnotationId(restored)
      if (highId > measurementIdRef.current) measurementIdRef.current = highId
      if (highId > probeIdRef.current) probeIdRef.current = highId
      seriesIdRef.current = nextSeriesId
      annotationStashGenerationRef.current = currentStashGeneration

      // In-progress tools never carry across real series hops; completed marks come from the stash.
      setMeasurementDraft(null)
      setMeasurementTool(null)
      setProbeTool(false)
      setProbeHover(null)
      setAnnotationFlash(null)
      angleBuildRef.current = null
      interactionRef.current = null
      setCinePlaying(false)
      if (!viewControlled || resetControlledViewOnVolumeChange) {
        onViewTransformChangeRef.current?.(FIT_VIEW)
        setLocalView(FIT_VIEW)
      }
      setPanning(false)
    }, [volume.seriesId])

    // Persist on ordinary remounts; a session-boundary generation change suppresses stale writes.
    useEffect(() => {
      return () => {
        const activeId = seriesIdRef.current
        if (!activeId) return
        if (
          annotationStashGenerationRef.current
          !== annotationStashGeneration(annotationStashRef.current)
        ) return
        stashSeriesAnnotations(annotationStashRef.current, activeId, {
          measurements: measurementsRef.current,
          pinnedProbes: pinnedProbesRef.current,
        })
      }
    }, [])

    useEffect(() => {
      if (!annotationFlash) return
      const token = annotationFlash.token
      const timer = window.setTimeout(() => {
        setAnnotationFlash((current) => (current?.token === token ? null : current))
        setActivePickFlash((current) => (current?.token === token ? null : current))
      }, 900)
      return () => window.clearTimeout(timer)
    }, [annotationFlash])

    // AC-2: Delete / Backspace removes only the selected inventory mark (flash selection).
    useEffect(() => {
      if (!annotationFlash) return
      const { kind, id, token } = annotationFlash
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Delete' && event.key !== 'Backspace') return
        if (event.metaKey || event.ctrlKey || event.altKey) return
        if (isTextEntryTarget(event.target)) return
        event.preventDefault()
        if (kind === 'measurement') {
          setMeasurements((current) => current.filter((measurement) => measurement.id !== id))
        } else {
          setPinnedProbes((current) => current.filter((probe) => probe.id !== id))
        }
        setAnnotationFlash(null)
        setActivePickFlash((current) => (current?.token === token ? null : current))
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [annotationFlash])

    useEffect(() => {
      setCinePlaying(false)
    }, [viewerLayout])

    useEffect(() => {
      if (depth <= 1) setCinePlaying(false)
    }, [depth])

    useEffect(() => {
      const preservedTransition = preservedSliceTransitionRef.current
      if (
        preservedTransition
        && (
          safeIndex === preservedTransition.interim
          || safeIndex === preservedTransition.expected
        )
      ) {
        if (safeIndex === preservedTransition.expected) {
          preservedSliceTransitionRef.current = null
        }
        return
      }
      preservedSliceTransitionRef.current = null
      setMeasurementDraft(null)
      angleBuildRef.current = null
      setProbeHover(null)
      if (interactionRef.current?.type === 'measurement') interactionRef.current = null
    }, [safeIndex])

    // Keep live probe display gray in sync when W/L or invert changes under a parked cursor.
    useEffect(() => {
      setProbeHover((current) => {
        if (!current) return null
        const sample = samplePixelAt(
          volume,
          safeIndex,
          current.x,
          current.y,
          volumeSettings.window,
          volumeSettings.level,
          invertDisplay,
        )
        if (!sample) return null
        return { x: current.x, y: current.y, sample }
      })
    }, [safeIndex, volume, volumeSettings.level, volumeSettings.window, invertDisplay])

    useEffect(() => {
      if (!cinePlaying || depth <= 1) return
      const intervalMs = 1000 / cineFps
      const timer = window.setInterval(() => {
        const current = sliceIndexRef.current
        onSliceChange(current >= depth - 1 ? 0 : current + 1)
      }, intervalMs)
      return () => window.clearInterval(timer)
    }, [cinePlaying, cineFps, depth, onSliceChange])

    useEffect(() => {
      if (!pickFlash) return
      setCinePlaying(false)
      setActivePickFlash(pickFlash)
      const timer = window.setTimeout(() => {
        setActivePickFlash((current) => (
          current?.token === pickFlash.token ? null : current
        ))
      }, 900)
      return () => window.clearTimeout(timer)
    }, [pickFlash])

    /** User-driven slice change — pauses cine playback. */
    const setSliceFromUser = (index: number) => {
      setCinePlaying(false)
      onSliceChange(Math.max(0, Math.min(depth - 1, index)))
    }

    const stepSlice = (amount: number) => {
      setSliceFromUser(safeIndex + amount)
    }

    const resetView = () => {
      setView(FIT_VIEW)
      setPanning(false)
      if (interactionRef.current?.type === 'pan') interactionRef.current = null
    }

    const cropPoint = (event: React.PointerEvent) => {
      const canvas = canvasRef.current
      if (!canvas) return { x: 0, y: 0 }
      // Post-transform screen rect keeps measure/crop coords correct under pan/zoom.
      const bounds = canvas.getBoundingClientRect()
      if (bounds.width <= 0 || bounds.height <= 0) return { x: 0, y: 0 }
      return {
        x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
        y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
      }
    }

    const beginWindowLevel = (event: React.PointerEvent<HTMLDivElement>) => {
      interactionRef.current = {
        type: 'window-level',
        originX: event.clientX,
        originY: event.clientY,
        startWindow: volumeSettings.window,
        startLevel: volumeSettings.level,
      }
      setWindowLevelDrag({ window: volumeSettings.window, level: volumeSettings.level })
      setProbeHover(null)
      event.currentTarget.setPointerCapture(event.pointerId)
      event.preventDefault()
    }

    const pinProbeAt = (point: MeasurementPoint): PixelProbeSample | null => {
      const sample = samplePixelAt(
        volume,
        safeIndex,
        point.x,
        point.y,
        volumeSettings.window,
        volumeSettings.level,
        invertDisplay,
      )
      if (!sample) return null
      probeIdRef.current += 1
      setPinnedProbes((current) => [
        ...current,
        {
          id: probeIdRef.current,
          slice: safeIndex,
          x: point.x,
          y: point.y,
          sample,
        },
      ])
      return sample
    }

    const updateProbeHover = (event: React.PointerEvent<HTMLDivElement>) => {
      // Hide while panning, measuring, cropping drag, or window/level drag.
      if (interactionRef.current || measurementDraft || windowLevelDrag || panning) {
        setProbeHover(null)
        return
      }
      const point = cropPoint(event)
      const sample = samplePixelAt(
        volume,
        safeIndex,
        point.x,
        point.y,
        volumeSettings.window,
        volumeSettings.level,
        invertDisplay,
      )
      if (!sample) {
        setProbeHover(null)
        return
      }
      setProbeHover({ x: point.x, y: point.y, sample })
    }

    const beginInteraction = (event: React.PointerEvent<HTMLDivElement>) => {
      const wantsWindowLevel = event.button === 2 || (event.button === 0 && event.shiftKey)
      if (wantsWindowLevel) {
        beginWindowLevel(event)
        return
      }
      if (event.button !== 0) return

      const point = cropPoint(event)
      if (probeTool) {
        const sample = pinProbeAt(point)
        if (sample) setProbeHover({ x: point.x, y: point.y, sample })
        event.preventDefault()
        return
      }
      if (measurementTool === 'angle') {
        // Three clicks: arm end → vertex → arm end. Ref tracks progress across events.
        setProbeHover(null)
        event.preventDefault()
        const build = angleBuildRef.current
        if (!build || build.slice !== safeIndex) {
          angleBuildRef.current = { slice: safeIndex, start: point }
          setMeasurementDraft({
            id: -1,
            tool: 'angle',
            slice: safeIndex,
            start: point,
            end: point,
          })
          return
        }
        if (!build.vertex) {
          angleBuildRef.current = { ...build, vertex: point }
          setMeasurementDraft({
            id: -1,
            tool: 'angle',
            slice: safeIndex,
            start: build.start,
            vertex: point,
            end: point,
          })
          return
        }
        const final: Measurement = {
          id: -1,
          tool: 'angle',
          slice: safeIndex,
          start: build.start,
          vertex: build.vertex,
          end: point,
        }
        if (angleIsLargeEnough(final, width, height)) {
          measurementIdRef.current += 1
          setMeasurements((current) => [...current, {
            ...final,
            id: measurementIdRef.current,
          }])
        }
        angleBuildRef.current = null
        setMeasurementDraft(null)
        return
      }
      if (measurementTool) {
        const draft: Measurement = {
          id: -1,
          tool: measurementTool,
          slice: safeIndex,
          start: point,
          end: point,
        }
        interactionRef.current = {
          type: 'measurement',
          tool: measurementTool,
          slice: safeIndex,
          start: point,
        }
        setMeasurementDraft(draft)
        setProbeHover(null)
        event.currentTarget.setPointerCapture(event.pointerId)
        return
      }
      if (cropEditing) {
        const tolerance = 0.055
        const corners = [
          ['nw', cropBounds.minX, cropBounds.minY],
          ['ne', cropBounds.maxX, cropBounds.minY],
          ['sw', cropBounds.minX, cropBounds.maxY],
          ['se', cropBounds.maxX, cropBounds.maxY],
        ] as const
        const corner = corners.find(([, x, y]) => Math.abs(point.x - x) < tolerance && Math.abs(point.y - y) < tolerance)
        if (corner) interactionRef.current = { type: 'resize', corner: corner[0], bounds: cropBounds }
        else if (
          point.x >= cropBounds.minX && point.x <= cropBounds.maxX &&
          point.y >= cropBounds.minY && point.y <= cropBounds.maxY &&
          cropBounds.maxX - cropBounds.minX < 0.995
        ) interactionRef.current = { type: 'move', startX: point.x, startY: point.y, bounds: cropBounds }
        else interactionRef.current = { type: 'draw', startX: point.x, startY: point.y }
        event.currentTarget.setPointerCapture(event.pointerId)
        return
      }
      if (viewRef.current.scale > MIN_VIEW_SCALE) {
        interactionRef.current = {
          type: 'pan',
          startClientX: event.clientX,
          startClientY: event.clientY,
          originX: viewRef.current.x,
          originY: viewRef.current.y,
        }
        setPanning(true)
        event.currentTarget.setPointerCapture(event.pointerId)
      }
    }

    const updateInteraction = (event: React.PointerEvent<HTMLDivElement>) => {
      const interaction = interactionRef.current
      if (!interaction) {
        if (angleBuildRef.current || measurementDraft?.tool === 'angle') {
          const point = cropPoint(event)
          setMeasurementDraft((prev) => (
            prev?.tool === 'angle' ? { ...prev, end: point } : prev
          ))
          return
        }
        updateProbeHover(event)
        return
      }
      if (probeHover) setProbeHover(null)
      if (interaction.type === 'window-level') {
        const scaleX = Math.max(120, canvasRect?.width ?? 240)
        const scaleY = Math.max(120, canvasRect?.height ?? 240)
        const nextWindow = clamp01(
          interaction.startWindow + ((event.clientX - interaction.originX) / scaleX) * 1.15,
          0.1,
          1,
        )
        const nextLevel = clamp01(
          interaction.startLevel - ((event.clientY - interaction.originY) / scaleY) * 1.15,
        )
        setWindowLevelDrag({ window: nextWindow, level: nextLevel })
        onVolumeSettingsChange({ window: nextWindow, level: nextLevel })
        return
      }
      if (interaction.type === 'pan') {
        setView({
          scale: viewRef.current.scale,
          x: interaction.originX + (event.clientX - interaction.startClientX),
          y: interaction.originY + (event.clientY - interaction.startClientY),
        })
        return
      }
      const point = cropPoint(event)
      if (interaction.type === 'measurement') {
        setMeasurementDraft({
          id: -1,
          tool: interaction.tool,
          slice: interaction.slice,
          start: interaction.start,
          end: point,
        })
        return
      }
      if (!cropEditing) return
      const minimum = 0.035
      if (interaction.type === 'draw') {
        const minX = Math.min(interaction.startX, point.x)
        const maxX = Math.max(interaction.startX, point.x)
        const minY = Math.min(interaction.startY, point.y)
        const maxY = Math.max(interaction.startY, point.y)
        if (maxX - minX >= minimum && maxY - minY >= minimum) {
          onCropChange({ ...cropBounds, minX, maxX, minY, maxY })
        }
      } else if (interaction.type === 'move') {
        const boxWidth = interaction.bounds.maxX - interaction.bounds.minX
        const boxHeight = interaction.bounds.maxY - interaction.bounds.minY
        const minX = Math.max(0, Math.min(1 - boxWidth, interaction.bounds.minX + point.x - interaction.startX))
        const minY = Math.max(0, Math.min(1 - boxHeight, interaction.bounds.minY + point.y - interaction.startY))
        onCropChange({ ...cropBounds, minX, maxX: minX + boxWidth, minY, maxY: minY + boxHeight })
      } else {
        const next = { ...interaction.bounds }
        if (interaction.corner.includes('w')) next.minX = Math.min(point.x, next.maxX - minimum)
        if (interaction.corner.includes('e')) next.maxX = Math.max(point.x, next.minX + minimum)
        if (interaction.corner.includes('n')) next.minY = Math.min(point.y, next.maxY - minimum)
        if (interaction.corner.includes('s')) next.maxY = Math.max(point.y, next.minY + minimum)
        onCropChange(next)
      }
    }

    const finishInteraction = (event: React.PointerEvent<HTMLDivElement>) => {
      const interaction = interactionRef.current
      if (interaction?.type === 'window-level') {
        setWindowLevelDrag(null)
        interactionRef.current = null
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId)
        }
        return
      }
      if (interaction?.type === 'measurement' && measurementDraft) {
        const pixelWidth = Math.abs(measurementDraft.end.x - measurementDraft.start.x) * width
        const pixelHeight = Math.abs(measurementDraft.end.y - measurementDraft.start.y) * height
        const largeEnough = measurementDraft.tool === 'distance'
          ? Math.hypot(pixelWidth, pixelHeight) >= 3
          : pixelWidth >= 3 && pixelHeight >= 3
        if (largeEnough) {
          measurementIdRef.current += 1
          setMeasurements((current) => [...current, {
            ...measurementDraft,
            id: measurementIdRef.current,
          }])
        }
        setMeasurementDraft(null)
      }
      if (interaction?.type === 'pan') setPanning(false)
      interactionRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    }

    const cancelInteraction = () => {
      if (interactionRef.current?.type === 'pan') setPanning(false)
      interactionRef.current = null
      angleBuildRef.current = null
      setMeasurementDraft(null)
      setWindowLevelDrag(null)
      setProbeHover(null)
    }

    const clearProbeHover = () => {
      setProbeHover(null)
    }

    const handleWheel = (event: React.WheelEvent<HTMLElement>) => {
      if ((event.target as HTMLElement).closest('.annotation-inventory-list')) return
      event.preventDefault()
      if (event.ctrlKey || event.metaKey) {
        const viewport = viewportRef.current
        const stage = stageRef.current
        if (!viewport || !stage) return
        const bounds = viewport.getBoundingClientRect()
        // Anchor on the stage's layout center — its transform-origin. Layout offsets are
        // pre-transform and relative to `.slice-viewport` (position: relative), the same
        // origin as the cursor coords below. The viewport's border-box center would be
        // wrong: its vertical padding is asymmetric, so the centered stage sits below it.
        const anchorX = stage.offsetLeft + stage.offsetWidth * 0.5
        const anchorY = stage.offsetTop + stage.offsetHeight * 0.5
        const factor = event.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP
        setView((current) => zoomAboutPoint(
          current,
          current.scale * factor,
          event.clientX - bounds.left,
          event.clientY - bounds.top,
          anchorX,
          anchorY,
        ))
        return
      }
      stepSlice(event.deltaY > 0 ? 1 : -1)
    }

    const selectMeasurementTool = (tool: MeasurementTool) => {
      const next = measurementTool === tool ? null : tool
      setMeasurementTool(next)
      setMeasurementDraft(null)
      angleBuildRef.current = null
      setProbeTool(false)
      interactionRef.current = null
      if (next) onCropEditingChange(false)
    }

    const toggleInvertDisplay = () => {
      setInvertDisplay((current) => !current)
    }

    const toggleProbeTool = () => {
      const next = !probeTool
      setProbeTool(next)
      setMeasurementTool(null)
      setMeasurementDraft(null)
      angleBuildRef.current = null
      interactionRef.current = null
      if (next) onCropEditingChange(false)
    }

    const currentMeasurements = measurements.filter((measurement) => measurement.slice === safeIndex)
    const visibleMeasurements = measurementDraft?.slice === safeIndex
      ? [...currentMeasurements, measurementDraft]
      : currentMeasurements
    const currentPinnedProbes = pinnedProbes.filter((probe) => probe.slice === safeIndex)
    const hasSliceAnnotations = currentMeasurements.length > 0 || currentPinnedProbes.length > 0
    const hasSeriesAnnotations = measurements.length > 0 || pinnedProbes.length > 0

    const annotationInventory = useMemo<AnnotationInventoryItem[]>(() => {
      const showScalar =
        Math.abs(volume.scalarRange[1] - volume.scalarRange[0] - 255) > 1
        || Math.abs(volume.scalarRange[0]) > 0.5
      const items: AnnotationInventoryItem[] = [
        ...measurements.map((measurement) => {
          const flash = measurementFlashPoint(measurement)
          return {
            key: `m-${measurement.id}`,
            kind: 'measurement' as const,
            tool: measurement.tool,
            id: measurement.id,
            slice: measurement.slice,
            summary: measurementSummary(measurement, volume, width, height) || TOOL_LABELS[measurement.tool],
            flashX: flash.x,
            flashY: flash.y,
          }
        }),
        ...pinnedProbes.map((probe) => ({
          key: `p-${probe.id}`,
          kind: 'probe' as const,
          tool: 'probe' as const,
          id: probe.id,
          slice: probe.slice,
          summary: showScalar
            ? `I ${probe.sample.intensity} · ${formatProbeScalar(probe.sample.scalar)}`
            : `I ${probe.sample.intensity}`,
          flashX: probe.x,
          flashY: probe.y,
        })),
      ]
      items.sort((a, b) => {
        if (a.slice !== b.slice) return a.slice - b.slice
        if (TOOL_SORT_ORDER[a.tool] !== TOOL_SORT_ORDER[b.tool]) {
          return TOOL_SORT_ORDER[a.tool] - TOOL_SORT_ORDER[b.tool]
        }
        return a.id - b.id
      })
      return items
    }, [height, measurements, pinnedProbes, volume, width])

    const clearSliceAnnotations = () => {
      setMeasurements((current) => current.filter((measurement) => measurement.slice !== safeIndex))
      setPinnedProbes((current) => current.filter((probe) => probe.slice !== safeIndex))
    }

    const clearAllAnnotations = () => {
      setMeasurements([])
      setPinnedProbes([])
      setMeasurementDraft(null)
      angleBuildRef.current = null
      setAnnotationFlash(null)
    }

    /** Remove a single inventory mark without touching others on the slice or series. */
    const removeAnnotation = (item: AnnotationInventoryItem) => {
      if (item.kind === 'measurement') {
        setMeasurements((current) => current.filter((measurement) => measurement.id !== item.id))
      } else {
        setPinnedProbes((current) => current.filter((probe) => probe.id !== item.id))
      }
      const selectedFlash = annotationFlash
      if (selectedFlash && selectedFlash.kind === item.kind && selectedFlash.id === item.id) {
        setAnnotationFlash(null)
        setActivePickFlash((current) => (
          current?.token === selectedFlash.token ? null : current
        ))
      }
    }

    const jumpToAnnotation = (item: AnnotationInventoryItem) => {
      setCinePlaying(false)
      onSliceChange(Math.max(0, Math.min(depth - 1, item.slice)))
      // Negative tokens cannot collide with App's positive 3D-pick sequence.
      annotationFlashTokenRef.current -= 1
      const token = annotationFlashTokenRef.current
      setAnnotationFlash({ token, kind: item.kind, id: item.id })
      setActivePickFlash({ token, x: item.flashX, y: item.flashY })
    }

    const isFlashingMeasurement = (id: number) => (
      annotationFlash?.kind === 'measurement' && annotationFlash.id === id
    )
    const isFlashingProbe = (id: number) => (
      annotationFlash?.kind === 'probe' && annotationFlash.id === id
    )

    const cropped = cropBounds.minX > 0.001 || cropBounds.maxX < 0.999 ||
      cropBounds.minY > 0.001 || cropBounds.maxY < 0.999 ||
      cropBounds.minZ > 0.001 || cropBounds.maxZ < 0.999

    const canPan = view.scale > MIN_VIEW_SCALE && !cropEditing && !measurementTool && !probeTool
    const probeBlocked = Boolean(
      windowLevelDrag || panning || measurementDraft || interactionRef.current,
    )
    const liveProbe = !probeBlocked ? probeHover : null
    const showScalar = Math.abs(volume.scalarRange[1] - volume.scalarRange[0] - 255) > 1
      || Math.abs(volume.scalarRange[0]) > 0.5

    return (
      <section
        className={[
          'slice-viewer',
          paneLabel ? `pane-${paneLabel.toLowerCase()}` : '',
          hasPlaneSwitch ? 'has-plane-switch' : '',
        ].filter(Boolean).join(' ')}
        aria-label={paneLabel ? `2D DICOM slice viewer pane ${paneLabel}` : '2D DICOM slice viewer'}
        data-pane={paneLabel || undefined}
        data-view-transform={`${view.scale},${view.x},${view.y}`}
        data-window={volumeSettings.window}
        data-level={volumeSettings.level}
        data-invert={invertDisplay ? 'true' : 'false'}
        data-slice-plane={slicePlane}
        onWheel={handleWheel}
      >
        <div className="slice-viewport" ref={viewportRef}>
          {hasPlaneSwitch ? (
            <div className="mpr-plane-switch" role="group" aria-label="MPR plane">
              {MPR_PLANES.map(({ plane, label, shortLabel }) => {
                const isAcquired = acquiredPlane === plane
                return (
                  <button
                    key={plane}
                    type="button"
                    className={slicePlane === plane ? 'active' : ''}
                    aria-label={`${label} plane${isAcquired ? ' (acquired)' : ''}`}
                    aria-pressed={slicePlane === plane}
                    data-acquired={isAcquired ? 'true' : undefined}
                    title={`${label}${isAcquired ? ' · acquired' : ' reformat'}`}
                    onClick={() => {
                      setCinePlaying(false)
                      onSlicePlaneChange(plane)
                    }}
                  >
                    {shortLabel}
                  </button>
                )
              })}
            </div>
          ) : null}
          <div
            className={`slice-stage${canPan ? ' pannable' : ''}${panning ? ' panning' : ''}${windowLevelDrag ? ' window-leveling' : ''}${probeTool ? ' probing' : ''}`}
            ref={stageRef}
            data-testid="slice-stage"
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
              ...(windowLevelDrag ? { cursor: 'ns-resize' } : null),
              ...(probeTool && !windowLevelDrag ? { cursor: 'crosshair' } : null),
            }}
            onPointerDown={beginInteraction}
            onPointerMove={updateInteraction}
            onPointerUp={finishInteraction}
            onPointerCancel={cancelInteraction}
            onPointerLeave={clearProbeHover}
            onContextMenu={(event) => event.preventDefault()}
          >
            <canvas
              ref={canvasRef}
              data-testid="slice-canvas"
              aria-label={`Slice ${safeIndex + 1} of ${depth}`}
              style={{
                aspectRatio: `${horizontalSpanMillimeters} / ${verticalSpanMillimeters}`,
                objectFit: 'fill',
                ...(horizontalSpanMillimeters >= verticalSpanMillimeters
                  ? { width: '100%', height: 'auto' }
                  : { width: 'auto', height: '100%' }),
              }}
            />
            {canvasRect ? (
              <div
                className={`crop-overlay${cropEditing ? ' editing' : ''}${measurementTool ? ' measuring' : ''}${probeTool ? ' probing' : ''}${windowLevelDrag ? ' window-leveling' : ''}${canPan ? ' panning-ready' : ''}`}
                data-testid="crop-overlay"
                style={canvasRect}
              >
                <div
                  className="slice-scale-ruler ruler-horizontal"
                  aria-label={`${horizontalRulerMillimeters} millimeter horizontal scale`}
                  style={{
                    width: `${Math.min(45, (horizontalRulerMillimeters / horizontalSpanMillimeters) * 100)}%`,
                  }}
                >
                  <i />
                  <span>{horizontalRulerMillimeters} mm</span>
                </div>
                <div
                  className="slice-scale-ruler ruler-vertical"
                  aria-label={`${verticalRulerMillimeters} millimeter vertical scale`}
                  style={{
                    height: `${Math.min(45, (verticalRulerMillimeters / verticalSpanMillimeters) * 100)}%`,
                  }}
                >
                  <i />
                  <span>{verticalRulerMillimeters} mm</span>
                </div>
                {cropped || cropEditing ? (
                  <div
                    className="crop-selection"
                    style={{
                      left: `${cropBounds.minX * 100}%`,
                      top: `${cropBounds.minY * 100}%`,
                      width: `${(cropBounds.maxX - cropBounds.minX) * 100}%`,
                      height: `${(cropBounds.maxY - cropBounds.minY) * 100}%`,
                    }}
                  >
                    <i className="crop-handle handle-nw" /><i className="crop-handle handle-ne" />
                    <i className="crop-handle handle-sw" /><i className="crop-handle handle-se" />
                    <span>CROP VOLUME</span>
                  </div>
                ) : null}
                {visibleMeasurements.length ? (
                  <>
                    <svg
                      className="measurement-overlay"
                      viewBox={`0 0 ${width} ${height}`}
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      {visibleMeasurements.map((measurement) => {
                        const x1 = measurement.start.x * width
                        const y1 = measurement.start.y * height
                        const x2 = measurement.end.x * width
                        const y2 = measurement.end.y * height
                        const flashClass = isFlashingMeasurement(measurement.id) ? ' is-flashing' : ''
                        if (measurement.tool === 'distance') {
                          return (
                            <g key={measurement.id} className={`distance-measurement${flashClass}`}>
                              <line x1={x1} y1={y1} x2={x2} y2={y2} />
                              <circle cx={x1} cy={y1} r="3.5" />
                              <circle cx={x2} cy={y2} r="3.5" />
                            </g>
                          )
                        }
                        if (measurement.tool === 'angle') {
                          const vx = (measurement.vertex?.x ?? measurement.end.x) * width
                          const vy = (measurement.vertex?.y ?? measurement.end.y) * height
                          return (
                            <g key={measurement.id} className={`angle-measurement${flashClass}`}>
                              {measurement.vertex ? (
                                <>
                                  <line x1={x1} y1={y1} x2={vx} y2={vy} />
                                  <line x1={vx} y1={vy} x2={x2} y2={y2} />
                                  <circle cx={x1} cy={y1} r="3.5" />
                                  <circle cx={vx} cy={vy} r="3.5" />
                                  <circle cx={x2} cy={y2} r="3.5" />
                                </>
                              ) : (
                                <>
                                  <line x1={x1} y1={y1} x2={x2} y2={y2} />
                                  <circle cx={x1} cy={y1} r="3.5" />
                                  <circle cx={x2} cy={y2} r="3.5" />
                                </>
                              )}
                            </g>
                          )
                        }
                        return (
                          <rect
                            key={measurement.id}
                            className={`roi-measurement${flashClass}`}
                            x={Math.min(x1, x2)}
                            y={Math.min(y1, y2)}
                            width={Math.abs(x2 - x1)}
                            height={Math.abs(y2 - y1)}
                          />
                        )
                      })}
                    </svg>
                    {visibleMeasurements.map((measurement) => {
                      const summary = measurementSummary(measurement, volume, width, height)
                      if (!summary) return null
                      const labelX = measurement.tool === 'distance'
                        ? (measurement.start.x + measurement.end.x) * 0.5
                        : measurement.tool === 'angle'
                          ? (measurement.vertex?.x ?? measurement.end.x)
                          : Math.max(measurement.start.x, measurement.end.x)
                      const labelY = measurement.tool === 'distance'
                        ? (measurement.start.y + measurement.end.y) * 0.5
                        : measurement.tool === 'angle'
                          ? (measurement.vertex?.y ?? measurement.end.y)
                          : Math.min(measurement.start.y, measurement.end.y)
                      return (
                        <span
                          key={`label-${measurement.id}`}
                          className={`measurement-label ${measurement.tool}${isFlashingMeasurement(measurement.id) ? ' is-flashing' : ''}`}
                          style={{
                            left: `${Math.max(0.05, Math.min(0.95, labelX)) * 100}%`,
                            top: `${Math.max(0.05, Math.min(0.95, labelY)) * 100}%`,
                          }}
                        >
                          {summary}
                        </span>
                      )
                    })}
                  </>
                ) : null}
                {windowLevelDrag ? (
                  <div className="window-level-readout" data-testid="window-level-readout" role="status" aria-live="polite">
                    <span>W {Math.round(windowLevelDrag.window * 255)}</span>
                    <span>L {Math.round(windowLevelDrag.level * 255)}</span>
                  </div>
                ) : null}
                {currentPinnedProbes.map((probe) => (
                  <div
                    key={probe.id}
                    className={`pixel-probe-pin${isFlashingProbe(probe.id) ? ' is-flashing' : ''}`}
                    data-testid="pixel-probe-pin"
                    style={{
                      left: `${probe.x * 100}%`,
                      top: `${probe.y * 100}%`,
                    }}
                  >
                    <i className="pixel-probe-cross" aria-hidden="true" />
                    <span className="pixel-probe-pin-label">
                      I {probe.sample.intensity}
                      {showScalar ? ` · ${formatProbeScalar(probe.sample.scalar)}` : ''}
                      <small>
                        c {probe.sample.col} · r {probe.sample.row}
                      </small>
                    </span>
                  </div>
                ))}
                {liveProbe ? (
                  <div
                    className="pixel-probe-readout"
                    data-testid="pixel-probe-readout"
                    role="status"
                    aria-live="polite"
                    style={{
                      left: `${Math.min(0.72, Math.max(0.02, liveProbe.x + 0.03)) * 100}%`,
                      top: `${Math.min(0.78, Math.max(0.02, liveProbe.y + 0.03)) * 100}%`,
                    }}
                  >
                    <span>
                      I {liveProbe.sample.intensity}
                      <em>D {liveProbe.sample.display}</em>
                    </span>
                    {showScalar ? (
                      <span className="pixel-probe-scalar">
                        {formatProbeScalar(liveProbe.sample.scalar)}
                      </span>
                    ) : null}
                    <span className="pixel-probe-coords">
                      c {liveProbe.sample.col} · r {liveProbe.sample.row}
                    </span>
                  </div>
                ) : null}
                {activePickFlash ? (
                  <div
                    key={activePickFlash.token}
                    className="slice-pick-crosshair"
                    data-testid="slice-pick-crosshair"
                    aria-hidden="true"
                    style={{
                      left: `${activePickFlash.x * 100}%`,
                      top: `${activePickFlash.y * 100}%`,
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="measurement-tools-cluster">
            {hasSeriesAnnotations ? (
              <div
                className="annotation-inventory"
                data-testid="annotation-inventory"
                aria-label="Series annotations"
              >
                <div className="annotation-inventory-header">
                  <span>
                    Annotations
                    <b>{annotationInventory.length}</b>
                  </span>
                  <button
                    type="button"
                    className="annotation-inventory-clear-all"
                    aria-label="Clear all annotations on series"
                    title="Clear all measurements and pins on this series"
                    onClick={clearAllAnnotations}
                  >
                    Clear all
                  </button>
                </div>
                <ul className="annotation-inventory-list" aria-label="Series annotation list">
                  {annotationInventory.map((item) => {
                    const onCurrentSlice = item.slice === safeIndex
                    const selected = (
                      (item.kind === 'measurement' && isFlashingMeasurement(item.id))
                      || (item.kind === 'probe' && isFlashingProbe(item.id))
                    )
                    const toolLabel = TOOL_LABELS[item.tool]
                    return (
                      <li
                        key={item.key}
                        className={`annotation-inventory-row tool-${item.tool}${onCurrentSlice ? ' on-slice' : ''}${selected ? ' selected' : ''}`}
                      >
                        <button
                          type="button"
                          className={`annotation-inventory-jump${selected ? ' selected' : ''}`}
                          data-testid="annotation-inventory-row"
                          data-slice={item.slice}
                          aria-current={onCurrentSlice ? 'true' : undefined}
                          aria-label={`${toolLabel}, ${item.summary}, slice ${item.slice + 1}`}
                          title={`Jump to slice ${item.slice + 1}`}
                          onClick={() => jumpToAnnotation(item)}
                          onKeyDown={(event) => {
                            if (event.key !== 'Delete' && event.key !== 'Backspace') return
                            if (event.metaKey || event.ctrlKey || event.altKey) return
                            event.preventDefault()
                            event.stopPropagation()
                            removeAnnotation(item)
                          }}
                        >
                          <span className="annotation-inventory-tool">{toolLabel}</span>
                          <span className="annotation-inventory-summary">{item.summary}</span>
                          <span className="annotation-inventory-slice">SL {String(item.slice + 1).padStart(3, '0')}</span>
                        </button>
                        <button
                          type="button"
                          className="annotation-inventory-delete"
                          data-testid="annotation-inventory-delete"
                          aria-label={`Delete ${toolLabel} on slice ${item.slice + 1}`}
                          title={`Delete this ${toolLabel.toLowerCase()}`}
                          onClick={() => removeAnnotation(item)}
                        >
                          <Trash2 size={11} aria-hidden="true" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : null}
            <div className="measurement-toolbar" role="toolbar" aria-label="Slice measurement tools">
              <button
                type="button"
                className={measurementTool === 'distance' ? 'active' : ''}
                aria-label="Distance measurement"
                aria-pressed={measurementTool === 'distance'}
                title="Measure distance in millimeters"
                onClick={() => selectMeasurementTool('distance')}
              >
                <Ruler size={14} /><span>Distance</span>
              </button>
              <button
                type="button"
                className={measurementTool === 'roi' ? 'active' : ''}
                aria-label="ROI area measurement"
                aria-pressed={measurementTool === 'roi'}
                title="Measure ROI area, mean, SD, min–max"
                onClick={() => selectMeasurementTool('roi')}
              >
                <SquareDashed size={14} /><span>ROI</span>
              </button>
              <button
                type="button"
                className={measurementTool === 'angle' ? 'active' : ''}
                aria-label="Angle measurement"
                aria-pressed={measurementTool === 'angle'}
                title="Measure angle between two rays (three clicks)"
                onClick={() => selectMeasurementTool('angle')}
              >
                <DraftingCompass size={14} /><span>Angle</span>
              </button>
              <button
                type="button"
                className={probeTool ? 'active' : ''}
                aria-label="Pixel intensity probe"
                aria-pressed={probeTool}
                title="Probe pixel intensity (hover live; click to pin)"
                data-testid="probe-tool"
                onClick={toggleProbeTool}
              >
                <Crosshair size={14} /><span>Probe</span>
              </button>
              <button
                type="button"
                className={invertDisplay ? 'active invert-active' : ''}
                aria-label="Invert grayscale"
                aria-pressed={invertDisplay}
                title="Invert grayscale polarity (display only)"
                data-testid="invert-tool"
                onClick={toggleInvertDisplay}
              >
                <Contrast size={14} /><span>Invert</span>
              </button>
              <button
                type="button"
                aria-label="Clear measurements on slice"
                title="Clear measurements and pins on this slice"
                disabled={!hasSliceAnnotations}
                onClick={clearSliceAnnotations}
              >
                <Trash2 size={13} />
              </button>
              {viewTransformed ? (
                <button
                  type="button"
                  aria-label="Reset pan and zoom"
                  title="Reset pan and zoom (fit to view)"
                  data-testid="reset-view"
                  onClick={resetView}
                >
                  <Maximize2 size={13} /><span>Fit</span>
                </button>
              ) : null}
            </div>
          </div>
          {viewTransformed ? (
            <div className="slice-view-badge" data-testid="view-zoom-badge" aria-live="polite">
              {Math.round(view.scale * 100)}%
            </div>
          ) : null}
          <span className="orientation-marker marker-top">{labels.top}</span>
          <span className="orientation-marker marker-right">{labels.right}</span>
          <span className="orientation-marker marker-bottom">{labels.bottom}</span>
          <span className="orientation-marker marker-left">{labels.left}</span>
          <div className="slice-metadata slice-meta-left">
            {paneLabel ? <span className="slice-pane-badge" aria-hidden="true">{paneLabel}</span> : null}
            <span>{volume.description}</span>
            <b>{volume.orientation.toUpperCase()}</b>
            <small>{width} × {height}</small>
          </div>
          <div className="slice-metadata slice-meta-right">
            <span>SL {String(safeIndex + 1).padStart(3, '0')} / {String(depth).padStart(3, '0')}</span>
            <b>{(safeIndex * volume.spacing[2]).toFixed(1)} mm</b>
            <small>W {Math.round(volumeSettings.window * 255)} · L {Math.round(volumeSettings.level * 255)}</small>
          </div>
        </div>

        <div className="slice-controls">
          <button type="button" aria-label="Previous slice" onClick={() => stepSlice(-1)}>
            <ChevronDown size={15} />
          </button>
          <button
            type="button"
            className={cinePlaying ? 'active' : ''}
            aria-label={cinePlaying ? 'Pause cine' : 'Play cine'}
            aria-pressed={cinePlaying}
            title={cinePlaying ? 'Pause stack play' : 'Play through stack'}
            disabled={depth <= 1}
            onClick={toggleCine}
          >
            {cinePlaying ? <Pause size={15} /> : <Play size={15} />}
          </button>
          <div className="slice-slider">
            <span>
              <MousePointer2 size={12} />
              {cinePlaying
                ? `Cine · ${cineFps} fps · loops at ends`
                : 'Scroll slices · Ctrl/⌘+scroll zoom · drag to pan'}
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(0, depth - 1)}
              step={1}
              value={safeIndex}
              aria-label="Displayed slice"
              style={{ '--slice-progress': `${depth > 1 ? (safeIndex / (depth - 1)) * 100 : 0}%` } as React.CSSProperties}
              onChange={(event) => setSliceFromUser(Number(event.target.value))}
            />
          </div>
          <button type="button" aria-label="Next slice" onClick={() => stepSlice(1)}>
            <ChevronUp size={15} />
          </button>
          <label className="cine-fps">
            <span className="visually-hidden">Cine frames per second</span>
            <select
              aria-label="Cine frames per second"
              value={cineFps}
              disabled={depth <= 1}
              onChange={(event) => setCineFps(Number(event.target.value) as CineFps)}
            >
              {CINE_FPS_OPTIONS.map((fps) => (
                <option key={fps} value={fps}>{fps} fps</option>
              ))}
            </select>
          </label>
          {!hideCropControls ? (
            <div className="slice-crop-actions">
              <button
                className={cropEditing ? 'active' : ''}
                type="button"
                aria-pressed={cropEditing}
                onClick={() => {
                  setMeasurementTool(null)
                  setMeasurementDraft(null)
                  angleBuildRef.current = null
                  setProbeTool(false)
                  onCropEditingChange(!cropEditing)
                }}
              >
                <Crop size={14} /><span>Crop 3D</span>
              </button>
              {cropped ? (
                <button
                  type="button"
                  aria-label="Reset volume crop"
                  title="Reset volume crop"
                  onClick={() => onCropChange({
                    minX: 0,
                    maxX: 1,
                    minY: 0,
                    maxY: 1,
                    minZ: 0,
                    maxZ: 1,
                  })}
                >
                  <RotateCcw size={13} />
                </button>
              ) : null}
            </div>
          ) : null}
          <output>{safeIndex + 1}<small> / {depth}</small></output>
        </div>
      </section>
    )
  },
)
